import React, {useEffect, useState, useContext} from 'react';
import {
  Platform,
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {hideMessage, showMessage} from 'react-native-flash-message';

import {leaveRoom} from 'store/communication/actions/controlActions';
import {goToLanding} from 'store/app';
import {I18nContext} from 'i18n/context/I18nContext';
import {logEvent} from 'utils/firebase/analytics';
import {MEETING_CONNECTION_FAILURE} from 'utils/firebase/analytics.config';
import {ROOM_STATE} from 'utils/communication/utils';

import { TYPE, FONTS } from 'style/typography';

const isWeb = Platform.OS === 'web';

const ConnectionFailureOverlay = () => {
  const dispatch = useDispatch();
  const {translate} = useContext(I18nContext);

  const {source, reason, message} = useSelector(
    (state) => state.communication.room.failure,
  );
  const roomState = useSelector((state) => state.communication.room.state);

  const [shouldDisplay, setShouldDisplay] = useState(
    source && roomState === ROOM_STATE.closed,
  );

  // The connection is dead at this point, so tearing down and returning to the
  // landing screen leaves the world half-initialized and the loading bar parked
  // at its cap. A full reload is the only reliable way back in.
  const onClose = async () => {
    await logEvent(MEETING_CONNECTION_FAILURE, {
      source: source,
      reason: reason,
      message: message,
    });

    if (isWeb) {
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
      return;
    }

    dispatch(leaveRoom());
    dispatch(goToLanding());
  };

  useEffect(() => {
    switch (roomState) {
      case ROOM_STATE.disconnected: {
        showMessage({
          message: translate('connectionFailureOverlay.poorConnection'),
          icon: 'auto',
          renderFlashMessageIcon: () => (
            <View>
              <ActivityIndicator color="white" />
            </View>
          ),
          autoHide: false,
          type: 'warning',
          floating: true,
          style: {width: 250},
          titleStyle: {alignSelf: 'center'},
          hideOnPress: false,
          position: 'top',
        });
        break;
      }
      case ROOM_STATE.closed: {
        hideMessage();
        break;
      }
      default: {
        break;
      }
    }
  }, [roomState, translate]);

  useEffect(() => {
    setShouldDisplay(source && roomState === ROOM_STATE.closed);
  }, [source, roomState, setShouldDisplay]);

  return shouldDisplay ? (
    <View style={styles.container} pointerEvents="auto">
      <View style={styles.card}>
        <Text style={styles.title}>{translate('stuckLoading.title')}</Text>
        <Text style={styles.message}>
          {isWeb
            ? translate('stuckLoading.message')
            : translate('stuckLoading.messageNative')}
        </Text>
        <TouchableOpacity
          style={styles.button}
          onPress={onClose}
          activeOpacity={0.85}>
          <Text style={styles.buttonText}>
            {isWeb
              ? translate('stuckLoading.refresh')
              : translate('stuckLoading.restart')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  ) : null;
};

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 10000,
    elevation: 10000,
    backgroundColor: 'rgba(0,0,0,0.85)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    maxWidth: 340,
    width: '100%',
    alignItems: 'center',
    backgroundColor: 'rgba(15,16,20,0.98)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.30)',
    borderRadius: 18,
    paddingVertical: 28,
    paddingHorizontal: 26,
  },
  title: {
    color: '#F6FBFF',
    fontWeight: '700',
    fontSize: TYPE.lg,
    fontFamily: FONTS.heading,
    textAlign: 'center',
  },
  message: {
    color: 'rgba(246,251,255,0.75)',
    fontWeight: '500',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    lineHeight: 20,
    textAlign: 'center',
    marginTop: 12,
  },
  button: {
    marginTop: 22,
    backgroundColor: '#C5E836',
    borderRadius: 24,
    paddingVertical: 12,
    paddingHorizontal: 32,
  },
  buttonText: {
    color: '#0F1014',
    fontWeight: '700',
    fontSize: TYPE.base,
    fontFamily: FONTS.body,
  },
});

export default ConnectionFailureOverlay;
